import { useState } from "react";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { formatDate } from "utils/format";

const EMPTY_ACTION_FILTER = { name: "", keyword: "", from: "", to: "", adminOnly: false };

function ActionHistory() {
  const [filter, setFilter] = useState(EMPTY_ACTION_FILTER);
  const [applied, setApplied] = useState(EMPTY_ACTION_FILTER);
  const [page, setPage] = useState(1);
  const size = 50;
  const { data, loading, error } = useFetch(
    () => api.get("/api/admin/history", { ...applied, page, size }),
    [applied, page]
  );
  const set = (key) => (e) => setFilter({ ...filter, [key]: e.target.value });

  return (
    <PageCard
      title="Lịch sử hoạt động"
      subtitle="Bảng history trong DB log, mới nhất trước"
      color="dark"
      noPadding
    >
      <MDBox
        px={2}
        pb={1}
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setApplied({ ...filter });
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <MDInput fullWidth label="Tên nhân vật" value={filter.name} onChange={set("name")} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MDInput
              fullWidth
              label="Từ khoá trong nội dung"
              value={filter.keyword}
              onChange={set("keyword")}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <MDInput
              fullWidth
              type="datetime-local"
              label="Từ"
              InputLabelProps={{ shrink: true }}
              value={filter.from}
              onChange={set("from")}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <MDInput
              fullWidth
              type="datetime-local"
              label="Đến"
              InputLabelProps={{ shrink: true }}
              value={filter.to}
              onChange={set("to")}
            />
          </Grid>
          <Grid item xs="auto">
            <FormControlLabel
              control={
                <Switch
                  checked={filter.adminOnly}
                  onChange={(e) => setFilter({ ...filter, adminOnly: e.target.checked })}
                />
              }
              label="Chỉ thao tác web admin"
            />
          </Grid>
          <Grid item xs="auto">
            <MDButton type="submit" variant="gradient" color="info">
              Lọc
            </MDButton>
          </Grid>
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {data && !data.hasTime && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Bảng history chưa có cột thời gian nên không hiện giờ và không lọc theo ngày được. Chạy
            Database/history_time.sql trên DB log để bật.
          </Alert>
        )}
      </MDBox>
      <AdminTable
        loading={loading}
        rows={data?.items || []}
        rowKey="id"
        page={page}
        size={size}
        hasMore={data?.hasMore}
        onPageChange={setPage}
        emptyText="Không có bản ghi"
        columns={[
          { key: "id", label: "#", width: 70 },
          { key: "time", label: "Thời gian", width: 170, render: (r) => formatDate(r.time) },
          {
            key: "name",
            label: "Nhân vật",
            render: (r) =>
              r.userId > 0 ? (
                <Link to={`/players/${r.userId}`}>
                  <MDTypography variant="button" color="info">
                    {r.name || `#${r.userId}`}
                  </MDTypography>
                </Link>
              ) : (
                r.name || "-"
              ),
          },
          {
            key: "log",
            label: "Nội dung",
            wrap: true,
            render: (r) => (
              <MDBox display="flex" gap={0.5} alignItems="flex-start">
                <span>{r.log}</span>
                {r.obj && (
                  <Tooltip
                    title={
                      <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontSize: 11 }}>{r.obj}</pre>
                    }
                    componentsProps={{ tooltip: { sx: { maxWidth: 600 } } }}
                  >
                    <Icon fontSize="small" color="action" sx={{ cursor: "help" }}>
                      data_object
                    </Icon>
                  </Tooltip>
                )}
              </MDBox>
            ),
          },
        ]}
      />
    </PageCard>
  );
}

const EMPTY_LOGIN_FILTER = { username: "", ip: "", failedOnly: false };

function LoginHistory() {
  const [filter, setFilter] = useState(EMPTY_LOGIN_FILTER);
  const [applied, setApplied] = useState(EMPTY_LOGIN_FILTER);
  const [page, setPage] = useState(1);
  const size = 50;
  const { data, loading, error } = useFetch(
    () => api.get("/api/admin/history/logins", { ...applied, page, size }),
    [applied, page]
  );

  return (
    <PageCard title="Lịch sử đăng nhập" subtitle="Cả game và web" color="info" noPadding>
      <MDBox
        px={2}
        pb={1}
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setApplied({ ...filter });
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <MDInput
              fullWidth
              label="Tên tài khoản (chính xác)"
              value={filter.username}
              onChange={(e) => setFilter({ ...filter, username: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <MDInput
              fullWidth
              label="IP (chính xác)"
              value={filter.ip}
              onChange={(e) => setFilter({ ...filter, ip: e.target.value })}
            />
          </Grid>
          <Grid item xs="auto">
            <FormControlLabel
              control={
                <Switch
                  checked={filter.failedOnly}
                  onChange={(e) => setFilter({ ...filter, failedOnly: e.target.checked })}
                />
              }
              label="Chỉ lần thất bại"
            />
          </Grid>
          <Grid item xs="auto">
            <MDButton type="submit" variant="gradient" color="info">
              Lọc
            </MDButton>
          </Grid>
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </MDBox>
      <AdminTable
        loading={loading}
        rows={data?.items || []}
        page={page}
        size={size}
        hasMore={data?.hasMore}
        onPageChange={setPage}
        emptyText="Không có bản ghi"
        columns={[
          { key: "time", label: "Thời gian", render: (r) => formatDate(r.time) },
          {
            key: "username",
            label: "Tài khoản",
            render: (r) => (
              <MDTypography
                variant="button"
                color="info"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  const next = { ...EMPTY_LOGIN_FILTER, username: r.username };
                  setFilter(next);
                  setApplied(next);
                  setPage(1);
                }}
              >
                {r.username}
              </MDTypography>
            ),
          },
          {
            key: "ip",
            label: "IP",
            render: (r) => (
              <MDTypography
                variant="button"
                color="info"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  const next = { ...EMPTY_LOGIN_FILTER, ip: r.ip };
                  setFilter(next);
                  setApplied(next);
                  setPage(1);
                }}
              >
                {r.ip}
              </MDTypography>
            ),
          },
          {
            key: "success",
            label: "Kết quả",
            render: (r) => (
              <Chip
                size="small"
                color={r.success ? "success" : "error"}
                label={r.success ? "Thành công" : "Thất bại"}
              />
            ),
          },
          { key: "web", label: "Nguồn", render: (r) => (r.web ? "Web" : "Game") },
        ]}
      />
    </PageCard>
  );
}

function History() {
  const [tab, setTab] = useState(0);
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="history" />
        <Card sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(e, value) => setTab(value)}>
            <Tab label="Hoạt động" />
            <Tab label="Đăng nhập" />
          </Tabs>
        </Card>
        {tab === 0 ? <ActionHistory /> : <LoginHistory />}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default History;
